import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Card, Table, Button, Input, Select, Space, Tag, Modal, Form,
  InputNumber, Row, Col, Typography, Tooltip, Popconfirm, DatePicker,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined, LinkOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import axios from "axios";
import dayjs from "dayjs";
import {
  getAllNonConsumables,
  createNonConsumable,
  updateNonConsumable,
  deleteNonConsumable,
} from "../../api/inventoryApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Text } = Typography;
const { Search } = Input;

const ASSET_TYPES = [
  { value: "OFFSET_PLATE", label: "Offset Printing Plate" },
  { value: "DIE", label: "Die" },
  { value: "PRINTING_SCREEN", label: "Printing Screen" },
  { value: "FLEXO_BLOCK", label: "Flexo Printing Block" },
];

const CONDITIONS = [
  { value: "GOOD", label: "Good", color: "green" },
  { value: "WORN", label: "Worn", color: "orange" },
  { value: "DAMAGED", label: "Damaged", color: "red" },
  { value: "RETIRED", label: "Retired", color: "default" },
];

const NonConsumableList = () => {
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState(undefined);

  // Modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Running sizes for linking
  const [runningSizes, setRunningSizes] = useState([]);
  const [loadingRS, setLoadingRS] = useState(false);

  // ── Fetch data ─────────────────────────────────────────────
  const fetchAssets = useCallback(async (page = 1, limit = 50) => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (searchText) params.search = searchText;
      if (filterType) params.type = filterType;
      const result = await getAllNonConsumables(params);
      setData(result?.data || []);
      const pg = result?.pagination || {};
      setPagination({
        current: pg.page || page,
        pageSize: pg.limit || limit,
        total: pg.total || 0,
      });
    } catch (error) {
      console.error(error.message);
      toast.error("Failed to load non-consumable assets");
    } finally {
      setLoading(false);
    }
  }, [searchText, filterType]);

  const fetchRunningSizes = async () => {
    setLoadingRS(true);
    try {
      const { data } = await axios.get("running-size?query=all");
      setRunningSizes(data || []);
    } catch (error) {
      console.error("Failed to load running sizes", error);
    }
    setLoadingRS(false);
  };

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  if (!isLogged) return <Navigate to="/auth/login" replace />;
  checkTokenExp(accessToken);

  // ── Handlers ───────────────────────────────────────────────
  const handleTableChange = (pag) => fetchAssets(pag.current, pag.pageSize);

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ type: "OFFSET_PLATE", condition: "GOOD" });
    fetchRunningSizes();
    setModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      name: record.name,
      type: record.type,
      condition: record.condition,
      running_size_ids: record.running_size_ids || [],
      location: record.location,
      purchase_cost: record.purchase_cost,
      vendor: record.vendor,
      purchase_date: record.purchase_date ? dayjs(record.purchase_date) : undefined,
      max_usage: record.max_usage,
      notes: record.notes,
      // config fields
      size: record.config?.size,
      material: record.config?.material,
      description: record.config?.description,
    });
    fetchRunningSizes();
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        name: values.name,
        type: values.type,
        condition: values.condition || "GOOD",
        running_size_ids: values.running_size_ids || [],
        location: values.location,
        purchase_cost: values.purchase_cost,
        vendor: values.vendor,
        purchase_date: values.purchase_date?.toISOString(),
        max_usage: values.max_usage,
        notes: values.notes,
        config: {
          size: values.size,
          material: values.material,
          description: values.description,
        },
      };

      if (editingRecord) {
        await updateNonConsumable(editingRecord.id, payload);
        toast.success("Asset updated");
      } else {
        await createNonConsumable(payload);
        toast.success("Asset created");
      }
      setModalVisible(false);
      fetchAssets(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error?.errorFields) return;
      console.error(error.message);
      toast.error(editingRecord ? "Failed to update" : "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteNonConsumable(record.id);
      toast.success("Asset deactivated");
      fetchAssets(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error(error.message);
      toast.error("Failed to deactivate asset");
    }
  };

  // ── Columns ────────────────────────────────────────────────
  const columns = [
    {
      title: "Code", dataIndex: "code", key: "code", width: 150,
      render: (val) => <Text strong>{val || "—"}</Text>,
    },
    { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
    {
      title: "Type", dataIndex: "type", key: "type", width: 180,
      render: (val) => {
        const t = ASSET_TYPES.find((a) => a.value === val);
        return <Tag color="blue">{t?.label || val || "—"}</Tag>;
      },
    },
    {
      title: "Condition", dataIndex: "condition", key: "condition", width: 100,
      render: (val) => {
        const c = CONDITIONS.find((x) => x.value === val);
        return <Tag color={c?.color || "default"}>{c?.label || val}</Tag>;
      },
    },
    {
      title: "Linked Running Sizes", key: "running_sizes", width: 250, ellipsis: true,
      render: (_, record) => {
        const rs = record.running_sizes || [];
        if (!rs.length) return <Text type="secondary">None</Text>;
        return (
          <Space size={[0, 4]} wrap>
            {rs.slice(0, 3).map((r) => <Tag key={r.id} icon={<LinkOutlined />}>{r.name}</Tag>)}
            {rs.length > 3 && <Tag>+{rs.length - 3} more</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Usage", key: "usage", width: 100,
      render: (_, record) => {
        const max = record.max_usage;
        return max ? `${record.usage_count || 0} / ${max}` : `${record.usage_count || 0}`;
      },
    },
    { title: "Location", dataIndex: "location", key: "location", width: 120, render: (v) => v || "—" },
    {
      title: "Cost", dataIndex: "purchase_cost", key: "purchase_cost", width: 100,
      render: (val) => val != null ? `₹${Number(val).toFixed(0)}` : "—",
    },
    {
      title: "Actions", key: "actions", width: 100, fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          <Popconfirm title="Deactivate this asset?" onConfirm={() => handleDelete(record)} okText="Yes" cancelText="No">
            <Tooltip title="Deactivate">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────
  return (
    <div>
      <PageTitle title="Back" subtitle="Non-Consumables" />

      <Card
        title={
          <Space>
            <span>Non-Consumable Assets</span>
            <Tag>{pagination.total} total</Tag>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Add Asset
          </Button>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={10} md={8}>
            <Search
              placeholder="Search by name, code, vendor..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => fetchAssets(1)}
              enterButton={<SearchOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Select
              placeholder="Asset Type"
              allowClear
              style={{ width: "100%" }}
              value={filterType}
              onChange={(val) => setFilterType(val)}
              options={ASSET_TYPES}
            />
          </Col>
          <Col>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={() => fetchAssets(1)} />
            </Tooltip>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={data}
          rowKey={(r) => r.id || r.code}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100],
            showTotal: (total) => `Total ${total} assets`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* ── Create / Edit Modal ────────────────────────────── */}
      <Modal
        title={editingRecord ? `Edit — ${editingRecord.code || ""}` : "Add Non-Consumable Asset"}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        width={720}
        okText={editingRecord ? "Update" : "Create"}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="type" label="Asset Type" rules={[{ required: true }]}>
                <Select options={ASSET_TYPES} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="condition" label="Condition">
                <Select options={CONDITIONS.map((c) => ({ value: c.value, label: c.label }))} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="name" label="Name / Description" rules={[{ required: true, message: "Enter asset name" }]}>
                <Input placeholder="e.g. Offset Plate – Goodcook Mixing Bowl 3qt" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="size" label="Size / Dimensions">
                <Input placeholder="e.g. 20x25 inch" />
              </Form.Item>
            </Col>
          </Row>

          {/* Link to Running Sizes */}
          <Form.Item name="running_size_ids" label="Linked Running Sizes (shared across these products)">
            <Select
              mode="multiple"
              showSearch
              allowClear
              loading={loadingRS}
              placeholder="Search and select running sizes..."
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={runningSizes.map((rs) => ({
                value: rs.id,
                label: `${rs.name}${rs.customer?.name ? ` (${rs.customer.name})` : ""}`,
              }))}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Text strong style={{ display: "block", marginBottom: 12, marginTop: 8 }}>
            Purchase & Storage
          </Text>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="purchase_cost" label="Purchase Cost (₹)">
                <InputNumber min={0} step={1} style={{ width: "100%" }} placeholder="5000" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="vendor" label="Vendor">
                <Input placeholder="Vendor name" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="purchase_date" label="Purchase Date">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="location" label="Storage Location">
                <Input placeholder="e.g. Rack A-3" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="max_usage" label="Max Usage Count">
                <InputNumber min={0} style={{ width: "100%" }} placeholder="e.g. 50000" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="material" label="Material">
                <Input placeholder="e.g. Aluminium, Steel" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={1} placeholder="Any additional notes..." />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default NonConsumableList;
